// v51 — Sidebar. Dark control-room shell + a rail filter.
//
// THREE CHANGES, and the first one is why the other two are safe:
//
//   1. The rail wears `lc-v2`, which is a SCOPED token block in index.css.
//      CSS custom properties on :root cascade to the whole document, so
//      re-tuning the tokens globally would retint all 117 routes in one
//      commit. Putting them on a class means only this subtree moves, and
//      every other screen keeps today's skin until it opts in.
//
//   2. Brand + filter are FIXED; only the nav list scrolls. The brand was
//      already outside the scroll container; the filter joins it there, so
//      the two things you navigate WITH never scroll away from you.
//
//   3. The filter narrows the rail itself — it is not the Cmd-K palette.
//      Cmd-K searches the product; this searches the menu. They answer
//      different questions and are deliberately separate controls.
//
// v50.212 — Sidebar (nav redesign Phase 1).
// v50.227 — Apple-style expandable areas. Each area with secondary
// items now carries a disclosure chevron; clicking the chevron toggles
// the submenu independently of navigation. Active area still
// auto-expands so the current section is always disclosed. Manual
// toggles persist across navigations until toggled back.
// v50.228 — Bug fix: clicking the chevron on an area whose `secondary`
// items include the current pathname (e.g. Projects, which lists
// /requirements, /tenders, /deals, /futures) had no effect because
// `isActive` forced the panel open regardless of the user's toggle.
// Replaced the additive Set with a Map<string, boolean> so the
// chevron can hard-override the auto-expand in either direction.
//
// Renders the role-aware 8-area primary rail + the grouped secondary
// vertical sub-nav defined in navAreas.ts. Replaces the flat 14-item
// rail driven by the old nav.ts NAV_ITEMS.
//
// Behaviour summary (see Build notes/labourcore-nav-redesign-brief.md):
//   - Universal areas (Today, Inbox, Compliance, Payments, Insights,
//     Settings) never move; only positions 3-4 swap on View as.
//   - Active area: longest-prefix match against pathname, including
//     legacy routes and any secondary[].route.
//   - Secondary panel renders when the area is active OR manually
//     expanded via the disclosure chevron. Items are grouped by
//     `group`; items with `status: 'new'` render disabled with a
//     small "Soon" tag and don't navigate.
//   - Notifications is de-listed from the rail per §6 (still reachable
//     at /notifications and via the topbar bell in a follow-up ship).

import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { useShellUser } from "@/features/auth/AuthContext";
import { adminTierOf } from "@/features/admin/components/AdminGate";
import { useEffectiveRole, useRoleOverride, type RoleOverride } from "../roleOverride";
import { useSidebarBadges } from "../useSidebarBadges";
import { ProfileMenu } from "./ProfileMenu";
import { APP_VERSION } from "@/lib/version";
import { Icon } from "@/components/Icon";
import { TierNavigator, SpineMark } from "./TierNavigator";
import { useActingContext } from "@/lib/context/useActingContext";
import { ADMIN_NAV } from "../navAreas";
import { railFor, type RailItem, type RailSection } from "../railData";
import { RailIconGlyph } from "../railIcons";

interface SidebarProps {}

export function Sidebar(_props: SidebarProps = {}) {
  const user           = useShellUser();
  const navigate       = useNavigate();
  const location       = useLocation();
  const effectiveRole  = useEffectiveRole();
  const badges         = useSidebarBadges();

  /* TIER-DRIVEN, and transcribed from the client's prototype rather than
     derived from our own taxonomy — see the provenance note in railData.ts. */
  const { catalogue, actable, tier: actingTier } = useActingContext();
  const allSections = railFor(actingTier);

  /* Rail filter. Narrows the menu, not the product — Cmd-K does the latter
     and is mounted globally in App.tsx. */
  const [filter, setFilter] = useState("");
  const [tierNavOpen, setTierNavOpen] = useState(false);

  /* Rail collapse. Remembered, because it is a workspace preference rather
     than a per-visit decision — someone on a 13" screen wants it collapsed
     every time, not once. */
  const [railCollapsed, setRailCollapsed] = useRailCollapsed();
  /* v52 — offered whenever there is a catalogue to show, not only when there
     is somewhere to go. The navigator stopped being purely a switcher when it
     started stating the tier you are on and what the others cost: for a
     single-tier tenant that is orientation, and for an admin it is the demo
     surface. The rows still only ENTER what the server granted. */
  const showTierNav = catalogue.length > 0;
  const q = filter.trim().toLowerCase();

  /* The filter narrows ITEMS and drops any section left empty — a heading with
     nothing under it reads as a broken search rather than a narrowed one. */
  const sections: RailSection[] = !q
    ? allSections
    : allSections
        .map(sec => ({ ...sec, items: sec.items.filter(i => i.label.toLowerCase().includes(q)) }))
        .filter(sec => sec.items.length > 0);

  /* Longest-prefix match, so /projects/site-ops lights Site ops rather than
     Active projects. Exact-only would leave every child route with nothing
     highlighted; plain startsWith would light both. */
  /* EVERY ROUTE ON THE RAIL, CHILDREN INCLUDED.
     Three separate functions have now walked `sec.items` and quietly missed
     nested rows — SOON_ROUTES 404'd a child link, and the two candidate lists
     below made a child route invisible to the active check, so the parent lit
     instead. One flattener, used by all of them. */
  const allRoutes = (): string[] => {
    const out: string[] = [];
    const walk = (items: RailItem[]) => {
      for (const i of items) {
        out.push(i.route);
        if (i.children?.length) walk(i.children);
      }
    };
    for (const sec of allSections) walk(sec.items);
    return out;
  };

  const isActiveRoute = (route: string) => {
    const path = location.pathname;
    const [rPath, rQuery] = route.split("?");

    if (path !== rPath && !path.startsWith(rPath + "/")) return false;

    /* A route carrying a query string addresses a PANEL, not a page:
       /payments?tab=fees and /payments are two rail items on one screen. Match
       the params too, or Payments and Fee model both light at once. */
    if (rQuery) {
      const want = new URLSearchParams(rQuery);
      const have = new URLSearchParams(location.search);
      for (const [k, v] of want) if (have.get(k) !== v) return false;
      return true;
    }

    /* A bare route loses to any sibling panel-route that matched, so landing on
       /payments?tab=fees does not also light plain Payments. */
    const siblings = allRoutes().filter(r => r.startsWith(rPath + "?"));
    for (const sib of siblings) {
      const want = new URLSearchParams(sib.split("?")[1]);
      const have = new URLSearchParams(location.search);
      let all = true;
      for (const [k, v] of want) if (have.get(k) !== v) { all = false; break; }
      if (all) return false;
    }

    /* Longest prefix wins, so /projects/site-ops lights Site ops rather than
       Active projects. */
    const best = allRoutes()
      .map(r => r.split("?")[0])
      .filter(r => path === r || path.startsWith(r + "/"))
      .sort((a, b) => b.length - a.length)[0];
    return best === rPath;
  };

  /* Live counts beat the prototype's illustrative ones. Where we have no live
     source the transcribed number stands, so the rail still demos correctly. */
  const badgeFor = (item: RailItem): number => {
    if (item.route === "/inbox") return badges.inbox ?? item.badge ?? 0;
    return item.badge ?? 0;
  };

  /* Expandable rail rows (owner's Schemes). Keyed by label, absent means
     "follow the active child". */
  const [kidOpen, setKidOpen] = useState<Record<string, boolean>>({});

  /* v50.228 — user-toggled expansion overrides. Map<areaId, expanded?>.
     Absent → fall back to the auto-expand-on-active default.
     Present → hard override (even if the area is currently active).
     This lets the chevron close an active area whose secondary list
     happens to cover the current route (Projects's secondary covers
     /requirements, /tenders, /deals, /futures, etc — without the
     override the panel would stay forced open). */
  const [userExpanded, setUserExpanded] = useState<Map<string, boolean>>(() => new Map());

  /* v50.322 — admin panel disclosure. null → auto (open on /admin
     routes, closed elsewhere); true/false → user override. */
  const [adminOverride, setAdminOverride] = useState<boolean | null>(null);

  /* GH #231 — clear the manual overrides when the effective role changes.
     `areas` is rebuilt from the new role but `userExpanded` is keyed by area id,
     and several ids exist in both rails ("projects" among them). So an override
     set while viewing as Partner carried straight into Coord view and forced
     that panel open — which read as the sidebar expanding Projects by itself on
     every View-as switch. Switching view should hand back the default
     auto-expand-on-active behaviour, not inherit decisions made about a
     different rail. */
  useEffect(() => {
    setUserExpanded(new Map());
    setAdminOverride(null);
  }, [effectiveRole]);
  const toggleExpanded = (id: string, currentlyExpanded: boolean) => {
    setUserExpanded(prev => {
      const next = new Map(prev);
      next.set(id, !currentlyExpanded);
      return next;
    });
  };

  /* The area-model helpers lived here: badgeForArea, goSecondary and
     isSecondaryActive. All three were keyed to NavArea/SecondaryItem, which
     the rail no longer uses — badges now key off the item's route and there
     are no secondary items, because the prototype has one flat list per
     section rather than a two-level tree. Removed rather than left dangling. */


  return (
    <aside
      /* z-30 because `sticky` alone creates NO stacking context — without an
         explicit z-index, any later sibling that does create one paints over
         the rail. That is why the tier switcher was disappearing behind the
         supply pool's cards.

         30 is deliberate, not arbitrary: above page content, below the mobile
         header and bottom nav (z-40) and below every modal (z-50), so a dialog
         still covers the rail as it should. */
      className={`lc-v2 shrink-0 h-screen sticky top-0 z-30 flex flex-col transition-[width] duration-150 ${
        railCollapsed ? "w-[60px]" : "w-60"
      }`}
      data-collapsed={railCollapsed || undefined}
      style={{ background: "var(--lc-shell)", borderRight: "1px solid var(--lc-shell-line)" }}
    >
      {/* ── FIXED: brand + filter. Outside the scroll container on purpose —
             these are the two things you navigate with. ────────────────── */}
      <div className="shrink-0">
        <div
          className={`flex items-center min-w-0 ${railCollapsed ? "justify-center" : ""}`}
          style={{ padding: railCollapsed ? "18px 8px 14px" : "18px 18px 14px", gap: 9 }}
        >
          {/* The wordmark is DRAWN, not an image asset.
              The prototype sets it as inline SVG plus text — 24px mark, then
              LABOUR at 900/13.5px/.055em in #F1F5F9 with CORE in #60A5FA. The
              image we were serving rendered at its own weight and tracking and
              could not pick up the rail's colours, so it read as a different
              logo sitting next to the same product. These are the prototype's
              literal values. */}
          <RailBrand collapsed={railCollapsed} />

          {/* Collapse/expand the whole rail. A real panel glyph rather than
              chevrons: two arrowheads say "something moves", a panel with a
              closing edge says WHICH something. */}
          {!railCollapsed && (
            <button
              type="button"
              onClick={() => setRailCollapsed(true)}
              aria-label="Collapse navigation"
              title="Collapse navigation"
              className="shrink-0 p-1.5 rounded-lc-sm transition-colors"
              /* Pushed to the far edge rather than sitting against the
                 wordmark. The rail's right edge is where a collapse control is
                 looked for — it is the edge that moves — and letting it float
                 beside the logo reads as part of the brand lockup. */
              style={{ color: "var(--lc-shell-faint)", marginLeft: "auto", marginRight: "-4px" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--lc-shell-2)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = ""; }}
            >
              <PanelCollapseIcon />
            </button>
          )}
        </div>

        {railCollapsed && (
          <div className="px-2 pb-2 flex justify-center">
            <button
              type="button"
              onClick={() => setRailCollapsed(false)}
              aria-label="Expand navigation"
              title="Expand navigation"
              className="p-1.5 rounded-lc-sm transition-colors"
              style={{ color: "var(--lc-shell-faint)" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--lc-shell-2)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = ""; }}
            >
              <PanelCollapseIcon expand />
            </button>
          </div>
        )}

        <div className={`px-3 pb-3 ${railCollapsed ? "hidden" : ""}`}>
          <label className="sr-only" htmlFor="lc-rail-filter">Filter navigation</label>
          <div className="relative">
            <span
              className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "var(--lc-shell-faint)" }}
              aria-hidden="true"
            >
              <Icon name="search" size={14} />
            </span>
            <input
              id="lc-rail-filter"
              type="search"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              onKeyDown={e => { if (e.key === "Escape") setFilter(""); }}
              placeholder="Search…"
              autoComplete="off"
              className="w-full pl-8 pr-10 py-2 text-sm rounded-lc-md outline-none transition-colors"
              style={{
                background: "var(--lc-shell-2)",
                border: "1px solid var(--lc-shell-line)",
                color: "var(--lc-shell-fg)",
              }}
            />
            {/* The hint stays even while filtering: Cmd-K is a different
                control and remains available. */}
            <kbd
              className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-[10px] font-semibold px-1.5 py-0.5 rounded"
              style={{ color: "var(--lc-shell-faint)", border: "1px solid var(--lc-shell-line)" }}
            >
              ⌘K
            </kbd>
          </div>
        </div>
      </div>

      {/* The "Navigate Labour Core" button used to sit here. Removed: OPERATING
          AS in the foot opens the same modal and does it better, because it
          also STATES the tier you are on. Two entry points to one dialog, one
          of which said nothing about your current position, was a duplicate
          that cost a row of vertical space the nav could not spare. */}

      <TierNavigator open={tierNavOpen} onClose={() => setTierNavOpen(false)} />

      {/* ── SCROLLS: everything below ─────────────────────────────────── */}
      <nav
        aria-label="Primary navigation"
        className="flex-1 min-h-0 overflow-y-auto flex flex-col"
        style={{ padding: "2px 8px", gap: "1px" }}
      >
        {q && sections.length === 0 && (
          <p className="px-3 py-6 text-xs text-center" style={{ color: "var(--lc-shell-faint)" }}>
            Nothing in the menu matches “{filter}”.
          </p>
        )}

        {sections.map((sec, i) => (
          <div key={sec.g ?? `sec-${i}`} className="flex flex-col" style={{ gap: "1px" }}>
            {/* Named section → heading. Unnamed → a rule, but never above the
                first section, which would put a line under the search box. */}
            {!railCollapsed && (
              sec.g
                ? <div className="lc-nav-sec">{sec.g}</div>
                : i > 0 ? <div className="lc-nav-div" /> : null
            )}
            {railCollapsed && i > 0 && <div className="lc-nav-div" />}

            {sec.items.map(item => {
              const active = isActiveRoute(item.route);
              const live = badgeFor(item);

              /* EXPANDABLE ROW. A parent with children is a disclosure, not a
                 destination — pressing it opens the list instead of navigating,
                 so the owner never lands on a screen whose only job is to offer
                 three links. Auto-opens when a child is active, and the chevron
                 overrides that in either direction. */
              if (item.children?.length && !railCollapsed) {
                /* isActiveRoute now resolves the longest match across the whole
                   rail, children included, so it already answers this correctly
                   and the reduce that used to live here is gone. */
                const childActive = item.children.some(c => isActiveRoute(c.route));
                const open = kidOpen[item.label] ?? childActive;
                return (
                  <div key={item.route + item.label}>
                    <button
                      type="button"
                      onClick={() => setKidOpen(m => ({ ...m, [item.label]: !open }))}
                      aria-expanded={open}
                      className={`lc-srow w-full${childActive ? " lc-srow--active" : ""}`}
                    >
                      <span className="lc-srow-ic"><RailIconGlyph name={item.icon} /></span>
                      <span className="flex-1 min-w-0 truncate text-left">{item.label}</span>
                      <span className="shrink-0 text-[10px] transition-transform"
                            style={{ transform: open ? "rotate(90deg)" : "none",
                                     color: "var(--lc-shell-faint)" }}>›</span>
                    </button>
                    {open && (
                      <div className="ml-[26px] border-l pl-2 my-0.5"
                           style={{ borderColor: "var(--lc-shell-line)" }}>
                        {item.children.map(kid => (
                          <NavLink
                            key={kid.route + kid.label}
                            to={kid.route}
                            className={`lc-srow${isActiveRoute(kid.route) ? " lc-srow--active" : ""}`}
                          >
                            <span className="flex-1 min-w-0 truncate">{kid.label}</span>
                            {kid.soon && (
                              <span className="text-[8.5px] font-bold tracking-wider uppercase shrink-0"
                                    style={{ color: "var(--lc-shell-faint)" }}>Soon</span>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.route + item.label}
                  to={item.route}
                  title={railCollapsed ? item.label : undefined}
                  className={`lc-srow${active ? " lc-srow--active" : ""}`}
                  style={railCollapsed ? { justifyContent: "center", padding: "8px 0" } : undefined}
                >
                  <span className="lc-srow-ic"><RailIconGlyph name={item.icon} /></span>
                  {!railCollapsed && (
                    <>
                      <span className="flex-1 min-w-0 truncate">{item.label}</span>
                      {/* A placeholder is marked, not hidden — the rail should
                          read as complete while staying honest about what is
                          behind each row. */}
                      {item.soon && (
                        <span className="text-[8.5px] font-bold tracking-wider uppercase shrink-0"
                              style={{ color: "var(--lc-shell-faint)" }}>
                          Soon
                        </span>
                      )}
                      {!item.soon && live > 0 && (
                        <span className="lc-srow-badge">{live > 99 ? "99+" : live}</span>
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>



      {/* v49.69 / v49.77 / v49.79 / v50.321 — admin back-office links,
          rendered per platform-admin tier (master / board / investor).
          v50.322 — collapsible: the full link list (8 entries for master)
          was crowding the primary nav into a scroll. Auto-expands on
          /admin routes; the header chevron hard-overrides either way.
          v50.323 — restyled to match the primary nav areas: area-style
          header row (icon + soft active highlight + split chevron) and
          the links rendered like the grouped secondary panel. */}
      {adminTierOf(user) && !railCollapsed && (() => {
        const adminNav      = ADMIN_NAV[adminTierOf(user)!];
        const onAdminRoute  = location.pathname.startsWith("/admin");
        const adminExpanded = adminOverride !== null ? adminOverride : onAdminRoute;
        return (
          <div className="border-t border-lc-line px-2 py-2">
            <div
              className={`flex items-stretch rounded-lc-md transition-colors ${
                onAdminRoute ? "bg-lc-blue-light" : "hover:bg-lc-bg-2"
              }`}
            >
              <NavLink
                to={adminNav.links[0].to}
                className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-2 rounded-lc-md text-sm font-semibold transition-colors ${
                  onAdminRoute ? "text-lc-blue-dark" : "text-lc-ink-2"
                }`}
              >
                <span className={onAdminRoute ? "text-lc-blue-dark" : "text-lc-mute"}>
                  <Icon name="shield" size={16} />
                </span>
                <span className="flex-1 min-w-0 truncate">{adminNav.label}</span>
              </NavLink>
              <button
                type="button"
                onClick={() => setAdminOverride(!adminExpanded)}
                aria-expanded={adminExpanded}
                aria-controls="admin-nav-panel"
                aria-label={`${adminExpanded ? "Collapse" : "Expand"} ${adminNav.label} submenu`}
                className={`shrink-0 px-2 flex items-center justify-center rounded-r-lc-md transition-colors ${
                  onAdminRoute ? "text-lc-blue-dark hover:bg-lc-blue-light/70" : "text-lc-mute hover:text-lc-ink-2"
                }`}
              >
                <Icon
                  name="chevron-down"
                  size={12}
                  className={`transition-transform duration-150 ${adminExpanded ? "rotate-0" : "-rotate-90"}`}
                />
              </button>
            </div>
            {adminExpanded && (
              <div id="admin-nav-panel" className="ml-2 mt-1 mb-1 flex flex-col gap-0.5 border-l-2 border-lc-line pl-2">
                {adminNav.links.map(l => (
                  <NavLink key={l.to} to={l.to} end={l.to === "/admin"}
                    className={({ isActive }) =>
                      `flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lc-sm text-[12px] transition-colors ${
                        isActive
                          ? "bg-lc-blue-light text-lc-blue-dark font-extrabold"
                          : "text-lc-mute font-semibold hover:bg-lc-bg-2 hover:text-lc-ink"
                      }`
                    }>
                    {({ isActive }) => (
                      <>
                        <span className={`shrink-0 inline-block w-1 h-1 rounded-full ${isActive ? "bg-lc-blue" : "bg-transparent"}`} />
                        <span className="flex-1 min-w-0 truncate">{l.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* v53 — the acting context, as ONE collapsible group.
          View-as and Operating-as are two halves of the same answer to "who am
          I right now", and on a laptop they were eating the vertical space the
          nav needed — the rail was scrolling at four items. Grouped, and
          collapsed by default on a short viewport. */}
      {showTierNav && (
        <ActingContextGroup
          collapsed={railCollapsed}
          onOpenTierNav={() => setTierNavOpen(true)}
        />
      )}

      {/* Profile + sign-out — only renders when there's a user. */}
      {user && (
        <div className={`shrink-0 py-3 ${railCollapsed ? "px-1.5" : "px-3"}`}
             style={{ borderTop: "1px solid var(--lc-shell-line)" }}>
          <ProfileMenu compact={railCollapsed} />
        </div>
      )}

      {!railCollapsed && (
        <div className="shrink-0 px-5 py-3" style={{ borderTop: "1px solid var(--lc-shell-line)" }}>
          <div className="lc-eyebrow" style={{ color: "var(--lc-shell-faint)" }}>{APP_VERSION}</div>
        </div>
      )}
    </aside>
  );
}

/**
 * The LabourCore wordmark, at the prototype's own values.
 *
 * Note which bar is blue: the TOP one. The mark is the spine read downward —
 * owner at the head, operatives as the full-width foot — so colouring the head
 * is the one thing that must not drift.
 */
function RailBrand({ collapsed }: { collapsed: boolean }) {
  return (
    <>
      <svg viewBox="0 0 78 100" aria-hidden="true"
           style={{ height: 24, width: "auto", flexShrink: 0, display: "block" }}>
        <rect x="0" y="0"  width="32" height="19" rx="3" fill="#60A5FA" />
        <rect x="0" y="27" width="32" height="19" rx="3" fill="#F1F5F9" />
        <rect x="0" y="54" width="32" height="19" rx="3" fill="#F1F5F9" />
        <rect x="0" y="81" width="78" height="19" rx="3" fill="#F1F5F9" />
      </svg>
      {!collapsed && (
        <span style={{
          fontWeight: 900, fontSize: "13.5px", letterSpacing: ".055em",
          color: "#F1F5F9", lineHeight: 1, whiteSpace: "nowrap",
        }}>
          LABOUR<span style={{ color: "#60A5FA" }}>CORE</span>
        </span>
      )}
    </>
  );
}

/** A panel with a closing edge, not a pair of chevrons. Two arrowheads say
 *  "something moves"; a panel with a bar says WHICH something. */
function PanelCollapseIcon({ expand = false }: { expand?: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <line x1="9" y1="4" x2="9" y2="20" />
      {expand
        ? <polyline points="13.5 9.5 16 12 13.5 14.5" />
        : <polyline points="16 9.5 13.5 12 16 14.5" />}
    </svg>
  );
}

const RAIL_KEY = "lc_rail_collapsed_v1";

/** Remembered across sessions — a 13" screen wants it collapsed every time. */
function useRailCollapsed(): [boolean, (v: boolean) => void] {
  const [v, setV] = useState<boolean>(() => {
    try { return localStorage.getItem(RAIL_KEY) === "1"; } catch { return false; }
  });
  const set = (next: boolean) => {
    setV(next);
    try { localStorage.setItem(RAIL_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  };
  return [v, set];
}

/**
 * Is the viewport SHORT?
 *
 * Height, not width. The rail is a fixed-width column; what runs out on a 16"
 * laptop is vertical room, and a width query would happily call a 1512×982
 * MacBook "large" while its nav scrolls at four items.
 */
function useShortViewport(): boolean {
  const [short, setShort] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-height: 900px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-height: 900px)");
    const on = (e: MediaQueryListEvent) => setShort(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return short;
}

/**
 * THE ACTING CONTEXT — View-as and Operating-as, as one group.
 *
 * They answer the same question ("who am I acting as right now") from two
 * sides, and separately they were consuming enough of the foot that the nav
 * scrolled at four items on a laptop. So: one heading, one disclosure.
 *
 * Open on a tall screen, closed on a short one — the DEFAULT moves, never the
 * user's own choice. Once someone has toggled it, that decision holds; a panel
 * that reasserts itself on resize is a panel that ignores you.
 */
function ActingContextGroup({
  collapsed, onOpenTierNav,
}: { collapsed: boolean; onOpenTierNav: () => void }) {
  const short = useShortViewport();
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override !== null ? override : !short;

  /* Collapsed rail: no room for either block, and the tier is reachable from
     the profile popover. One icon that opens the navigator directly. */
  if (collapsed) {
    return (
      <div className="shrink-0 px-1.5 py-2 flex justify-center"
           style={{ borderTop: "1px solid var(--lc-shell-line)" }}>
        <button
          type="button"
          onClick={onOpenTierNav}
          aria-label="Switch tier"
          title="Switch tier"
          className="p-2 rounded-lc-sm transition-colors"
          style={{ color: "var(--lc-shell-fg)" }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--lc-shell-2)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = ""; }}
        >
          <SpineMark size={16} />
        </button>
      </div>
    );
  }

  /* Tall screen: no disclosure at all. A control that is always open is chrome
     the user has to read past, so it is not drawn. */
  if (!short) {
    return (
      <>
        <ViewAsSwitch />
        <OperatingAs onOpen={onOpenTierNav} />
      </>
    );
  }

  return (
    <div className="shrink-0" style={{ borderTop: "1px solid var(--lc-shell-line)" }}>
      <button
        type="button"
        onClick={() => setOverride(!open)}
        aria-expanded={open}
        aria-controls="acting-context-panel"
        className="w-full flex items-center gap-2 px-4 py-2.5 transition-colors"
        onMouseEnter={e => { e.currentTarget.style.background = "var(--lc-shell-2)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = ""; }}
      >
        <span className="lc-eyebrow flex-1 text-left" style={{ color: "var(--lc-shell-faint)" }}>
          Acting as
        </span>
        <span className="shrink-0" style={{ color: "var(--lc-shell-faint)" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
               style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform .15s" }}>
            <polyline points="7 13 12 18 17 13" />
            <polyline points="7 6 12 11 17 6" />
          </svg>
        </span>
      </button>

      {open && (
        <div id="acting-context-panel">
          <ViewAsSwitch />
          <OperatingAs onOpen={onOpenTierNav} />
        </div>
      )}
    </div>
  );
}

/**
 * OPERATING AS — the acting context in the sidebar foot.
 *
 * The authority badge and the tier block are separate on purpose. Authority
 * (owner / admin / member / viewer) says how much you may do; the tier says
 * what the company may act as. They are different axes, and a single combined
 * label is precisely the conflation this programme exists to undo.
 */
function OperatingAs({ onOpen }: { onOpen: () => void }) {
  const { tier, company, catalogue } = useActingContext();
  const user = useShellUser();

  const row = catalogue.find((t) => t.code === tier) ?? null;
  /* Authority still comes from the membership at the ACTIVE company even though
     the company itself is not drawn here — the badge is about this tenant, not
     about the account in general. */
  const authority = company?.role ?? null;
  const isPlatformAdmin = !!user?.isAdmin;

  /* Amber for the two levels that can change other people's access, slate for
     the two that cannot. The badge is a warning strip, not a rank display. */
  const AUTHORITY_TONE: Record<string, { bg: string; fg: string }> = {
    owner:  { bg: "rgb(232 180 92 / .16)", fg: "#e8b45c" },
    admin:  { bg: "rgb(232 180 92 / .16)", fg: "#e8b45c" },
    member: { bg: "rgb(255 255 255 / .07)", fg: "var(--lc-shell-mute)" },
    viewer: { bg: "rgb(255 255 255 / .07)", fg: "var(--lc-shell-faint)" },
  };
  const tone = (authority && AUTHORITY_TONE[authority]) || AUTHORITY_TONE.member;

  return (
    <div className="shrink-0 px-3 pt-3 pb-2">
      <div className="flex items-center justify-between px-1 mb-1.5">
        {/* "Operating as" was the group heading AND this row's label. With the
            group now named "Acting as", this row states the authority only. */}
        <span className="lc-eyebrow" style={{ color: "var(--lc-shell-faint)" }}>
          Authority
        </span>
        {authority && (
          <span
            className="text-[9.5px] font-extrabold tracking-wide px-1.5 py-0.5 rounded-lc-tag"
            style={{ background: tone!.bg, color: tone!.fg }}
          >
            {authority.toUpperCase()}
          </span>
        )}
      </div>

      {/* The tier, and the way into the navigator. */}
      <button
        type="button"
        onClick={onOpen}
        aria-haspopup="dialog"
        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lc-md transition-colors text-left"
        style={{
          background: "var(--lc-shell-2)",
          border: "1px solid var(--lc-shell-line)",
          color: "var(--lc-shell-fg)",
        }}
      >
        <span className="shrink-0" style={{ color: "var(--lc-shell-fg)" }}>
          <SpineMark size={17} />
        </span>
        <span className="min-w-0 flex-1 text-[12.5px] font-bold leading-tight">
          {row?.name ?? "No tier"}
        </span>
        <span className="shrink-0 inline-flex items-center gap-0.5 text-[11px] font-bold"
              style={{ color: "var(--lc-shell-accent)" }}>
          Switch
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      {isPlatformAdmin && (
        /* Said here as well as in the modal, because this is the line a support
           engineer reads while looking at a tenant's screen and wondering why
           they can see everything. */
        <p className="text-[10px] leading-snug mt-1.5 px-1" style={{ color: "var(--lc-shell-faint)" }}>
          Admin preview · this account can view all tiers. A live tenant
          switches only within its subscription.
        </p>
      )}

      {/* NO TENANT BLOCK HERE — deliberately.
          It was a third card stacked under two others saying much the same
          thing, and three cards in a sidebar foot is a wall, not a summary.
          The company now appears once, in the profile popover, alongside the
          authority badge and tier rank; that is where you look when you need to
          confirm which tenant you are in before acting. The tier button above
          already carries the only part you need at a glance. */}
    </div>
  );
}

/**
 * VIEW AS — which side of the marketplace, for a company holding both tiers.
 *
 * "Both" is gone, and it was not a styling decision. The option existed under
 * the v1 model where the override could be cleared; under tiers there is no
 * such state — a company always acts as exactly one tier at a time, and
 * `useRoleOverride` correctly no-ops on null. So the button had been inert
 * since the tier rewrite: present, styled, and incapable of doing anything.
 * A control that cannot act is worse than a missing one, because the user
 * blames themselves for the nothing that happens.
 */
function ViewAsSwitch() {
  const [override, setOverride] = useRoleOverride();
  const { actable, companyId } = useActingContext();

  /* Null means the active company holds only one of the two sides, so there is
     nothing to toggle between. */
  if (override === null) return null;

  /* Under demo mode a side can be present because the server widened the set,
     not because the company bought it. The switcher operates identically
     either way — but it says which is which, because a demonstrator needs to
     be able to point at what is genuinely in the plan. */
  const isDemo = (tier: "tier1" | "mep") =>
    actable.some(a => a.tier_code === tier && a.company_id === companyId && a.via_demo);

  const opts: { value: Exclude<RoleOverride, null>; label: string; hint: string }[] = [
    { value: "coordinator", label: "Buying",    hint: "Acting as Tier-1 — posting and awarding" },
    { value: "partner",     label: "Supplying", hint: "Acting as MEP — finding and bidding" },
  ];

  return (
    <div className="shrink-0 px-3 pt-3 pb-1">
      <div className="lc-eyebrow mb-1.5 px-1" style={{ color: "var(--lc-shell-faint)" }}>
        View as
      </div>
      <div
        className="grid grid-cols-2 gap-1 rounded-lc-md p-0.5"
        style={{ background: "var(--lc-shell-2)", border: "1px solid var(--lc-shell-line)" }}
        role="group"
        aria-label="Which side of the marketplace to act as"
      >
        {opts.map(o => {
          const active = override === o.value;
          return (
            <button
              key={o.value}
              type="button"
              title={o.hint}
              aria-pressed={active}
              onClick={() => setOverride(o.value)}
              className="px-2 py-1.5 rounded-lc-sm text-[11px] font-extrabold transition-colors"
              style={active
                ? { background: "var(--color-lc-blue)", color: "#fff" }
                : { color: "var(--lc-shell-mute)" }}
            >
              {o.label}
              {isDemo(o.value === "coordinator" ? "tier1" : "mep") && (
                <span className="ml-1 text-[8px] font-extrabold tracking-wide"
                      style={{ color: active ? "rgb(255 255 255 / .75)" : "var(--color-lc-amber)" }}>
                  DEMO
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
